import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import Link from "next/link";
import PopularVehiclesTable from "../TableComponents/PopularVehiclesTable";
import PricingTable from "../TableComponents/PricingTable";

const AtfContentSection = ({
  content,
  tableData = null, // { heading, vehiclesData }
  collapsedHeight = { xs: "96px", md: "51px" }, // Default collapsed height
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle different content formats
  const renderContent = () => {
    // If content is a string (simple text)
    if (typeof content === "string") {
      return (
        <Typography
          variant="body1"
          sx={{
            marginBottom: "10px",
            fontSize: { xs: "12px", md: "16px" },
            fontWeight: 400,
            color: "#000",
            lineHeight: 1.6,
          }}
        >
          {content}
        </Typography>
      );
    }

    // If content is an object with text array
    if (content?.text && Array.isArray(content.text)) {
      // If it has automakers, use the complex format
      if (content.automakers && content.automakers.length > 0) {
        const automakersIndex = content.automakersIndex !== undefined ? content.automakersIndex : 2;
        const separatorIndex = automakersIndex + 1;
        const endIndex = separatorIndex + 1;
        
        // Find the last rendered index to determine where to add <br />
        let lastRenderedIndex = -1;
        for (let i = content.text.length - 1; i >= 0; i--) {
          if (i !== separatorIndex && i !== endIndex) {
            lastRenderedIndex = i;
            break;
          }
        }
        
      return (
        <>
          {content.text.map((text, index) => {
              // Skip separator text and end text (they're included in the automakers paragraph)
              if (index === separatorIndex || index === endIndex) return null;

              // Handle the text index that should have automakers inserted
              if (index === automakersIndex) {
              return (
                <React.Fragment key={index}>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: "10px",
                      fontSize: { xs: "12px", md: "16px" },
                      fontWeight: 400,
                      color: "#000",
                      lineHeight: 1.6,
                    }}
                  >
                      {content.text[automakersIndex]}
                    {content.automakers?.map((automaker, automakerIndex) => (
                      <span key={automaker.name}>
                        <Link
                          href={automaker.link}
                          style={{
                            color: "#024670",
                            textDecoration: "none",
                            fontWeight: 600,
                            borderBottom: "1px solid #024670",
                          }}
                        >
                          {automaker.name}
                        </Link>
                        {automakerIndex < content.automakers.length - 2
                          ? ", "
                          : automakerIndex === content.automakers.length - 2
                            ? (content.text[separatorIndex] || ", and ")
                          : ""}
                      </span>
                    ))}
                      {content.text[endIndex] || ""}
                  </Typography>
                  {index < lastRenderedIndex && <br />}
                </React.Fragment>
              );
            }

            // Regular text paragraphs
            return (
              <React.Fragment key={index}>
                <Typography
                  variant="body1"
                  sx={{
                    marginBottom: index < content.text.length - 1 ? "10px" : "0",
                    fontSize: { xs: "12px", md: "16px" },
                    fontWeight: 400,
                    color: "#000",
                    lineHeight: 1.6,
                  }}
                >
                  {text}
                </Typography>
                {index < lastRenderedIndex && <br />}
              </React.Fragment>
            );
          })}
        </>
      );
      } else {
        // Simple text array without automakers
        return content.text.map((text, index) => (
          <React.Fragment key={index}>
            <Typography
              variant="body1"
              sx={{
                marginBottom: index < content.text.length - 1 ? "10px" : "0",
                fontSize: { xs: "12px", md: "16px" },
                fontWeight: 400,
                color: "#000",
                lineHeight: 1.6,
              }}
            >
              {text}
            </Typography>
            {index < content.text.length - 1 && <br />}
          </React.Fragment>
        ));
      }
    }

    // If content is an array of strings
    if (Array.isArray(content)) {
      return content.map((text, index) => (
        <Typography
          key={index}
          variant="body1"
          sx={{
            marginBottom: "10px",
            fontSize: { xs: "12px", md: "16px" },
            fontWeight: 400,
            color: "#000",
            lineHeight: 1.6,
          }}
        >
          {text}
        </Typography>
      ));
    }

    return null;
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "84%" },
        margin: "20px auto",
        padding: { xs: "0 12px", md: "0" },
      }}
    >
      <Box
        sx={{
          maxHeight: isExpanded ? "none" : collapsedHeight,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {renderContent()}

        {tableData && (
          <Box
            sx={{
              marginTop: "20px",
              display: isExpanded ? "block" : "none",
            }}
          >
            {tableData.type === "pricing" ? (
              <PricingTable
                heading={tableData.heading}
                columns={tableData.columns}
                data={tableData.data}
                inline={true}
              />
            ) : (
            <PopularVehiclesTable
              heading={tableData.heading}
              vehiclesData={tableData.vehiclesData}
              inline={true}
            />
            )}
          </Box>
        )}
      </Box>

      <Typography
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          marginTop: "10px",
          color: "#024670",
          fontSize: { xs: "12px", md: "14px" },
          fontWeight: "bold",
          cursor: "pointer",
          display: "inline-block",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {isExpanded ? "Read Less" : "Read More"}
      </Typography>
    </Box>
  );
};

export default AtfContentSection;

